import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, json, setCsrf } from "@/lib/api";
import type { User } from "@/lib/types";

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  login(
    values: { username: string; password: string },
    staff?: boolean,
  ): Promise<User>;
  register(values: {
    username: string;
    password: string;
    displayName?: string;
  }): Promise<User>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const accept = (next: User | null) => {
    setUser(next);
    setCsrf(next?.csrfToken);
  };
  const refresh = async () => {
    try {
      const result = await api<{ user: User }>("/auth/me");
      accept(result.user);
    } catch {
      accept(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void refresh();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      refresh,
      async login(values, staff = false) {
        const result = await api<{ user: User }>(
          staff ? "/auth/staff-login" : "/auth/login",
          { method: "POST", body: json(values) },
        );
        accept(result.user);
        return result.user;
      },
      async register(values) {
        const result = await api<{ user: User }>("/auth/register", {
          method: "POST",
          body: json(values),
        });
        accept(result.user);
        return result.user;
      },
      async logout() {
        await api("/auth/logout", { method: "POST" });
        accept(null);
      },
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be inside AuthProvider");
  return value;
}
